import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfBadge, EmptyState, PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY_IMG =
  "https://images.unsplash.com/photo-1498262257252-c282316270bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyYWwlMjBnZW9tZXRyaWN8ZW58MHx8fHwxNzg3NDkyNDQ5fDA&ixlib=rb-4.1.0&q=85";

export default function Documents() {
  const { tenantId, can, access } = useAuth();
  const [docs, setDocs] = useState([]);
  const [conf, setConf] = useState("1");
  const [framework, setFramework] = useState("none");
  const fileRef = useRef(null);

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/documents`).then(({ data }) => setDocs(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [tenantId]); // eslint-disable-line

  const upload = async (file) => {
    const form = new FormData();
    form.append("file", file);
    form.append("confidentiality", conf);
    form.append("document_class", "evidence");
    if (framework !== "none") form.append("framework", framework);
    try {
      await api.post(`/tenants/${tenantId}/documents`, form);
      toast.success("Document uploaded to secure storage");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/tenants/${tenantId}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        testid="documents-header"
        eyebrow="Shared service"
        title="Document centre"
        description="Evidence and governance artifacts. Download is a separate permission from view and is policy-gated per confidentiality class."
        actions={
          can("upload") && (
            <div className="flex flex-wrap items-center gap-3">
              <Select value={conf} onValueChange={setConf}>
                <SelectTrigger data-testid="upload-confidentiality-select" className="h-9 w-36 rounded-none text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {[1, 2, 3].filter((l) => l <= (access?.confidentiality_max || 1)).map((l) => (
                    <SelectItem key={l} value={String(l)}>{["", "General", "Sensitive", "Restricted"][l]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={framework} onValueChange={setFramework}>
                <SelectTrigger data-testid="upload-framework-select" className="h-9 w-40 rounded-none text-xs">
                  <SelectValue placeholder="Framework" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="none">No framework</SelectItem>
                  {(access?.frameworks || []).map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input
                ref={fileRef}
                type="file"
                data-testid="document-file-input"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
              />
              <Button
                data-testid="upload-document-button"
                className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]"
                onClick={() => fileRef.current?.click()}
              >
                Upload evidence
              </Button>
            </div>
          )
        }
      />

      <div className="panel divide-y divide-border" data-testid="documents-list">
        {docs.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div className="min-w-0">
              <p className="truncate text-sm">{d.name}</p>
              <p className="label-caps mt-1">
                {d.document_class} · {d.framework || "platform"} · {(d.size / 1024).toFixed(0)} KB · {d.uploaded_by_name}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <ConfBadge level={d.confidentiality} />
              {can("download") && (
                <Button
                  data-testid={`download-document-${d.id}`}
                  size="sm"
                  variant="outline"
                  className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                  onClick={() => download(d)}
                >
                  Download
                </Button>
              )}
            </div>
          </div>
        ))}
        {docs.length === 0 && (
          <div className="flex flex-col items-center gap-6 px-6 py-14 text-center" data-testid="documents-empty">
            <img src={EMPTY_IMG} alt="" className="h-40 w-full max-w-md object-cover grayscale" />
            <EmptyState title="No documents" message="Nothing has been uploaded at your clearance level yet." />
          </div>
        )}
      </div>
    </div>
  );
}
